import { Button, Chip, TableCell, TableRow, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import * as React from "react";
import { fetchData } from "../../../common/services/ApiClient";
import { ApiBaseUrl } from "../../../config";
import { KeyValue, Query, TableHeader } from "../../../interfaces";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import {
  additionalHeaderColor,
  textHeaderColor,
  secondaryHeaderColor,
  primaryHeaderColor,
} from "../../../refdata/constants";
import MultiHeaderTable from "../../MultiHeaderTable/MultiHeaderTable";
import generateBranchOfficeDepositsApiUrl, {
  combineStateData,
  filterDataForRow,
  getBranchCount,
  getMarketSelectTotals,
} from "./BranchOfficeDepositsUtilities";
import { chunkArray, reduceObj } from "../../../common/services/Utilities";
import CountyInstType from "./CountyInstType";

const PREFIX = "StateByInstType";

const classes = {
  openCloseAll: `${PREFIX}-openCloseAll`,
  tableRowStyles: `${PREFIX}-tableRowStyles`,
  chip: `${PREFIX}-chip`,
  rowIndent: `${PREFIX}-rowIndent`,
};

const Root = styled("div")(({ theme }) => ({
  [`& .${classes.openCloseAll}`]: {
    "minWidth": 80,
    "margin": 0,
    "color": "#ffffff",
    "fontWeight": 600,
    "backgroundColor": "#426cac",
    "&:hover, &.Mui-focusVisible": {
      color: theme.palette.primary.main,
      backgroundColor: theme.palette.secondary.main,
    },
  },

  [`& .${classes.tableRowStyles}`]: {
    "& td.MuiTableCell-root.MuiTableCell-body": {
      backgroundColor: "#acc7df",
      fontWeight: "bold",
    },
    "&:hover td.MuiTableCell-root.MuiTableCell-body, &:focus-visible td.MuiTableCell-root.MuiTableCell-body":
      {
        cursor: "pointer",
        filter: "brightness(.9)",
      },
  },

  [`& .${classes.chip}`]: {
    "cursor": "pointer",
    "backgroundColor": "transparent",
    "color": "#0000EE",
    "textDecoration": "underline",
    "fontSize": 14,
    "float": "left",
    "& .MuiChip-icon": {
      color: theme.palette.primary.main,
    },
  },

  [`& .${classes.rowIndent}`]: {
    "&.MuiTableCell-root.MuiTableCell-body": {
      textAlign: "left",
    },
  },
}));

interface Props {
  appState: Query;
}

const bankTypes = ["", "INSURED:CB", "INSURED:SA", "INSURED:IB"];

export default function StateInstType({ appState }: Props) {
  const [stateData, setStateData] = React.useState<KeyValue[]>([]);
  const [instTypeData, setInstTypeData] = React.useState<KeyValue>({
    COMMERCIAL: [],
    SAVINGS: [],
    BHC: [],
    FOREIGN: [],
  });

  const [previousSearch, setPreviousSearch] = React.useState<KeyValue>({});
  const initialTotalState = {
    COMMERCIAL: { count: 0, total: 0, sum_DEPSUMBR: 0 },
    SAVINGS: { count: 0, total: 0, sum_DEPSUMBR: 0 },
    FOREIGN: { count: 0, total: 0, sum_DEPSUMBR: 0 },
  };
  const [stateTotals, setStateTotals] =
    React.useState<KeyValue>(initialTotalState);

  const [openCloseHeader, setOpenCloseHeader] = React.useState<KeyValue>({});

  const openCloseButton = Object.keys(openCloseHeader).length > 1 && (
    <Button
      className={classes.openCloseAll}
      onClick={() => {
        Object.values(openCloseHeader).every((val) => val)
          ? setOpenCloseHeader(
              reduceObj(
                Object.keys(openCloseHeader).map((val) => {
                  return { key: val, value: false };
                }),
              ),
            )
          : setOpenCloseHeader(
              reduceObj(
                Object.keys(openCloseHeader).map((val) => {
                  return { key: val, value: true };
                }),
              ),
            );
      }}
    >
      {Object.values(openCloseHeader).every((val) => val)
        ? "Close All"
        : "Open All"}
    </Button>
  );
  const bhcByInstType: TableHeader[][] = [
    [
      {
        title: "",
        styles: {
          backgroundColor: primaryHeaderColor,
          color: textHeaderColor,
        },
        colSpan: 2,
      },
      {
        title: "Commercial Banks",
        styles: {
          backgroundColor: secondaryHeaderColor,
          color: textHeaderColor,
        },
        colSpan: 3,
      },
      {
        title: "Savings Institutions",
        styles: {
          backgroundColor: additionalHeaderColor,
          color: textHeaderColor,
        },
        colSpan: 3,
      },
      {
        title: "U.S. Branches of Foreign Banks",
        styles: {
          backgroundColor: secondaryHeaderColor,
          color: textHeaderColor,
        },
        colSpan: 3,
      },
    ],
    (() => {
      const headerValues = ["Institutions", "Offices", "Deposits"];

      const stateCountyHeader: TableHeader[] = [
        {
          title: "State / County",
          styles: {
            backgroundColor: primaryHeaderColor,
            color: textHeaderColor,
          },
          colSpan: 1,
          apiField: "",
        },
        {
          title: openCloseButton,
          styles: {
            backgroundColor: primaryHeaderColor,
            color: textHeaderColor,
          },
          colSpan: 1,
          apiField: "",
        },
      ];
      const commercialBanks: TableHeader[] = headerValues.map(
        (header: string) => {
          return {
            title: header,
            styles: {
              backgroundColor: secondaryHeaderColor,
              color: textHeaderColor,
            },
            colSpan: 1,
            apiField: "",
          };
        },
      );

      const savingsInstitutions: TableHeader[] = headerValues.map(
        (header: string) => {
          return {
            title: header,
            styles: {
              backgroundColor: additionalHeaderColor,
              color: textHeaderColor,
            },
            colSpan: 1,
            apiField: "",
          };
        },
      );

      const foreignBanks: TableHeader[] = headerValues.map((header: string) => {
        return {
          title: header,
          styles: {
            backgroundColor: secondaryHeaderColor,
            color: textHeaderColor,
          },
          colSpan: 1,
          apiField: "",
        };
      });

      return [
        ...stateCountyHeader,
        ...commercialBanks,
        ...savingsInstitutions,
        ...foreignBanks,
      ];
    })(),
  ];

  React.useEffect(() => {
    const filtersSearch = {
      filters: appState.filters,
      search: appState.search,
      sort: appState.sort,
    };
    if (
      appState.filters.bhcRSSD &&
      (Object.keys(appState.ui.marketSelection[0]).length === 0 ||
        appState.filters.stateNames[0] === "ALL") &&
      JSON.stringify(filtersSearch) !== JSON.stringify(previousSearch)
    ) {
      setPreviousSearch(filtersSearch);
      let isMounted = true;

      const fetchUrls = bankTypes.map((val: string) =>
        generateBranchOfficeDepositsApiUrl(
          appState,
          {
            by: "STNAMEBR",
            limit: 10000,
            sum: ["DEPSUMBR"],
          },
          undefined,
          undefined,
          "CERT",
          val,
        ),
      );

      (async () => {
        await Promise.all(
          fetchUrls.map((url: KeyValue) =>
            fetchData(`${ApiBaseUrl()}/sod`, url),
          ),
        )
          .then((response) => {
            if (isMounted) {
              const allStates = response[0].data.map((o: KeyValue) => {
                return { key: o.STNAMEBR, value: false };
              });
              setOpenCloseHeader(reduceObj(allStates));
              const commercialStates = response[1].data;
              const savingsStates = response[2].data;
              const foreignStates = response[3].data;

              setStateData(response[0].data);
              setInstTypeData({
                ...instTypeData,
                COMMERCIAL: filterDataForRow(
                  "STNAMEBR",
                  commercialStates,
                  response[0].data,
                ),
                SAVINGS: filterDataForRow(
                  "STNAMEBR",
                  savingsStates,
                  response[0].data,
                ),
                FOREIGN: filterDataForRow(
                  "STNAMEBR",
                  foreignStates,
                  response[0].data,
                ),
              });

              setStateTotals({
                ...stateTotals,
                COMMERCIAL: response[1].totals.count
                  ? {
                      ...response[1].totals,
                      total: getBranchCount(response[1].data),
                    }
                  : initialTotalState.COMMERCIAL,
                SAVINGS: response[2].totals.count
                  ? {
                      ...response[2].totals,
                      total: getBranchCount(response[2].data),
                    }
                  : initialTotalState.SAVINGS,
                FOREIGN: response[3].totals.count
                  ? {
                      ...response[3].totals,
                      total: getBranchCount(response[3].data),
                    }
                  : initialTotalState.FOREIGN,
              });
            }
          })
          .catch((error: Error) => {
            console.error(error);
          })
          .finally(() => (isMounted = false));
      })();
    }
  }, [
    appState,
    initialTotalState.COMMERCIAL,
    initialTotalState.FOREIGN,
    initialTotalState.SAVINGS,
    previousSearch,
    instTypeData,
    stateTotals,
  ]);

  React.useEffect(() => {
    const filtersSearch = {
      filters: appState.filters,
      search: appState.search,
      sort: appState.sort,
      marketSelect: appState.ui.marketSelection,
    };
    if (
      appState.filters.bhcRSSD &&
      Object.keys(appState.ui.marketSelection[0]).length > 0 &&
      appState.filters.stateNames[0] !== "ALL" &&
      JSON.stringify(filtersSearch) !== JSON.stringify(previousSearch)
    ) {
      setPreviousSearch(filtersSearch);
      let isMounted = true;
      const fetchUrls = appState.filters.stateNames
        .filter((state: string) => state)
        .map((state: string, index: number) => {
          return bankTypes.map((val: string) =>
            generateBranchOfficeDepositsApiUrl(
              appState,
              {
                by: "STNAMEBR",
                limit: 10000,
                terms: ["CERT"],
                sum: ["DEPSUMBR"],
              },
              state,
              appState.ui.marketSelection[index],
              "CERT",
              val,
            ),
          );
        });

      (async () => {
        await Promise.all(
          [].concat
            .apply([], fetchUrls)
            .map((url: KeyValue) => fetchData(`${ApiBaseUrl()}/sod`, url)),
        )
          .then((response) => {
            if (isMounted) {
              const splitResponse: KeyValue[] = chunkArray(response, 4);

              const getStateData = (resInt: number) => {
                const combineRes = (key: string) =>
                  [].concat.apply(
                    [],
                    splitResponse.map((res: KeyValue) => res[resInt][key]),
                  );
                return combineStateData(combineRes("data"), "STNAMEBR");
              };
              const allData = getStateData(0);

              const commercialData = getStateData(1);

              const savingsData = getStateData(2);

              const foreignData = getStateData(3);

              setOpenCloseHeader(
                reduceObj(
                  allData.map((data: KeyValue) => {
                    return { key: data.STNAMEBR, value: false };
                  }),
                ),
              );

              setStateData(allData);

              setInstTypeData({
                ...instTypeData,
                COMMERCIAL: filterDataForRow(
                  "STNAMEBR",
                  commercialData,
                  allData,
                ),
                SAVINGS: filterDataForRow("STNAMEBR", savingsData, allData),
                FOREIGN: filterDataForRow("STNAMEBR", foreignData, allData),
              });

              setStateTotals({
                ...stateTotals,
                COMMERCIAL:
                  commercialData.length > 0
                    ? getMarketSelectTotals(splitResponse, 1)
                    : initialTotalState.COMMERCIAL,
                SAVINGS:
                  savingsData.length > 0
                    ? getMarketSelectTotals(splitResponse, 2)
                    : initialTotalState.SAVINGS,
                FOREIGN:
                  foreignData.length > 0
                    ? getMarketSelectTotals(splitResponse, 3)
                    : initialTotalState.FOREIGN,
              });
            }
          })
          .catch((error: Error) => {
            console.error(error);
          })
          .finally(() => (isMounted = false));
      })();
    }
  }, [
    appState,
    initialTotalState.COMMERCIAL,
    initialTotalState.FOREIGN,
    initialTotalState.SAVINGS,
    previousSearch,
    instTypeData,
    stateTotals,
  ]);

  const stateRows = stateData.map((data, int) => {
    const expandCollapse = (e: any) => {
      const otherClicks = ["Open All", "Close All"];
      if (!otherClicks.includes(e.target.innerText)) {
        setOpenCloseHeader({
          ...openCloseHeader,
          [data.STNAMEBR]: !openCloseHeader[data.STNAMEBR] ? true : false,
        });
      }
    };

    return data && data.STNAMEBR ? (
      <React.Fragment key={`${data.STNAMEBR}_${int}`}>
        <TableRow
          id={`${data.STNAMEBR.toLowerCase().replace(/ /g, "")}_expand_${int}`}
          className={classes.tableRowStyles}
          tabIndex={0}
          onKeyPress={(e: any) => {
            if (e.key === "Enter") {
              expandCollapse(e);
            }
          }}
          onClick={(e: any) => expandCollapse(e)}
        >
          <TableCell>
            {openCloseHeader[data.STNAMEBR] ? (
              <Chip
                className={classes.chip}
                icon={<ExpandMoreIcon />}
                label={data.STNAMEBR}
              />
            ) : (
              <Chip
                className={classes.chip}
                icon={<ChevronRightIcon />}
                label={data.STNAMEBR}
              />
            )}
          </TableCell>
          <TableCell />
          <TableCell
            style={
              instTypeData.COMMERCIAL[int] &&
              instTypeData.COMMERCIAL[int].unique_count
                ? { color: "#0000EE", textDecoration: "underline" }
                : {}
            }
          >
            {instTypeData.COMMERCIAL[int]
              ? instTypeData.COMMERCIAL[int].unique_count
              : 0}
          </TableCell>
          <TableCell
            style={
              instTypeData.COMMERCIAL[int] && instTypeData.COMMERCIAL[int].count
                ? { color: "#0000EE", textDecoration: "underline" }
                : {}
            }
          >
            {instTypeData.COMMERCIAL[int]
              ? instTypeData.COMMERCIAL[int].count
              : 0}
          </TableCell>
          <TableCell
            style={
              instTypeData.COMMERCIAL[int] &&
              instTypeData.COMMERCIAL[int].sum_DEPSUMBR
                ? { color: "#0000EE", textDecoration: "underline" }
                : {}
            }
          >
            {instTypeData.COMMERCIAL[int] &&
            instTypeData.COMMERCIAL[int].sum_DEPSUMBR
              ? instTypeData.COMMERCIAL[int].sum_DEPSUMBR.toLocaleString()
              : 0}
          </TableCell>
          <TableCell
            style={
              instTypeData.SAVINGS[int] &&
              instTypeData.SAVINGS[int].unique_count
                ? { color: "#0000EE", textDecoration: "underline" }
                : {}
            }
          >
            {instTypeData.SAVINGS[int]
              ? instTypeData.SAVINGS[int].unique_count
              : 0}
          </TableCell>
          <TableCell
            style={
              instTypeData.SAVINGS[int] && instTypeData.SAVINGS[int].count
                ? { color: "#0000EE", textDecoration: "underline" }
                : {}
            }
          >
            {instTypeData.SAVINGS[int] ? instTypeData.SAVINGS[int].count : 0}
          </TableCell>
          <TableCell
            style={
              instTypeData.SAVINGS[int] &&
              instTypeData.SAVINGS[int].sum_DEPSUMBR
                ? { color: "#0000EE", textDecoration: "underline" }
                : {}
            }
          >
            {instTypeData.SAVINGS[int] && instTypeData.SAVINGS[int].sum_DEPSUMBR
              ? instTypeData.SAVINGS[int].sum_DEPSUMBR.toLocaleString()
              : 0}
          </TableCell>
          <TableCell
            style={
              instTypeData.FOREIGN[int] &&
              instTypeData.FOREIGN[int].unique_count
                ? { color: "#0000EE", textDecoration: "underline" }
                : {}
            }
          >
            {instTypeData.FOREIGN[int]
              ? instTypeData.FOREIGN[int].unique_count
              : 0}
          </TableCell>
          <TableCell
            style={
              instTypeData.FOREIGN[int] && instTypeData.FOREIGN[int].count
                ? { color: "#0000EE", textDecoration: "underline" }
                : {}
            }
          >
            {instTypeData.FOREIGN[int] ? instTypeData.FOREIGN[int].count : 0}
          </TableCell>
          <TableCell
            style={
              instTypeData.FOREIGN[int] &&
              instTypeData.FOREIGN[int].sum_DEPSUMBR
                ? { color: "#0000EE", textDecoration: "underline" }
                : {}
            }
          >
            {instTypeData.FOREIGN[int] && instTypeData.FOREIGN[int].sum_DEPSUMBR
              ? instTypeData.FOREIGN[int].sum_DEPSUMBR.toLocaleString()
              : 0}
          </TableCell>
        </TableRow>
        {openCloseHeader[data.STNAMEBR] && (
          <CountyInstType appState={appState} stateName={data.STNAMEBR} />
        )}
      </React.Fragment>
    ) : (
      <></>
    );
  });
  const totalRow = (
    <TableRow
      sx={{
        "& td.MuiTableCell-root.MuiTableCell-body": {
          backgroundColor: (theme) => theme.palette.primary.main,
          color: "#ffffff",
          fontWeight: "bold",
        },
      }}
    >
      <TableCell style={{ textAlign: "left", paddingLeft: "1rem" }}>
        Totals
      </TableCell>
      <TableCell />
      <TableCell>{stateTotals.COMMERCIAL.count.toLocaleString()}</TableCell>
      <TableCell>{stateTotals.COMMERCIAL.total.toLocaleString()}</TableCell>
      <TableCell>
        {stateTotals.COMMERCIAL.sum_DEPSUMBR.toLocaleString()}
      </TableCell>

      <TableCell>{stateTotals.SAVINGS.count.toLocaleString()}</TableCell>
      <TableCell>{stateTotals.SAVINGS.total.toLocaleString()}</TableCell>
      <TableCell>{stateTotals.SAVINGS.sum_DEPSUMBR.toLocaleString()}</TableCell>
      <TableCell>{stateTotals.FOREIGN.count.toLocaleString()}</TableCell>
      <TableCell>{stateTotals.FOREIGN.total.toLocaleString()}</TableCell>
      <TableCell>{stateTotals.FOREIGN.sum_DEPSUMBR.toLocaleString()}</TableCell>
    </TableRow>
  );
  return stateRows.length > 0 ? (
    <Root>
      <Typography
        variant="h4"
        color="primary.main"
        style={{ marginTop: "2rem" }}
      >
        State and County Totals by Institution Type
      </Typography>
      <MultiHeaderTable
        tableBody={
          <>
            {stateRows}
            {totalRow}
          </>
        }
        tableHeaders={bhcByInstType}
      />
    </Root>
  ) : (
    <></>
  );
}
